import React, { useMemo } from "react";
import dayjs from "dayjs";
import { useCustom, useTranslate } from "@refinedev/core";
import { StatSummary } from "@libertify/ui";
import type { ChatSummaryStats } from "types/";
import { ANALYTICS_STALE_TIME } from "pages/media/components/analytics/constants/constants";
import { ViewChatHistoryLink } from "pages/media/projects/components/ViewChatHistoryLink";
import {
  buildDateFilters,
  formatPct,
} from "pages/media/components/analytics/utils/";

const KEY = "experienceEdit.chatCard.overview";

/** Window the figures cover. */
const DAYS = 7;

const formatCount = (value?: number | null): string =>
  value == null ? "—" : value.toLocaleString();

const formatAverage = (value?: number | null): string =>
  value == null ? "—" : value.toFixed(1);

interface ChatbotOverviewProps {
  projectId?: string;
}

/**
 * Recent chat figures at the top of the edit page's chat card. A pulse, not a
 * dashboard — the full breakdown is the chat insights page.
 */
export const ChatbotOverview: React.FC<ChatbotOverviewProps> = ({
  projectId,
}) => {
  const t = useTranslate();
  // Fixed at mount, so the query key doesn't change on every render.
  const filters = useMemo(
    () =>
      buildDateFilters({
        startDate: dayjs().subtract(DAYS, "day"),
        endDate: dayjs(),
      }),
    []
  );

  const { data, isLoading } = useCustom<ChatSummaryStats>({
    url: `analytics/projects/${projectId}/analytics/chat/summary`,
    method: "get",
    config: { filters },
    queryOptions: {
      enabled: Boolean(projectId),
      staleTime: ANALYTICS_STALE_TIME,
    },
    // A project nobody has chatted with 404s; the tiles show "—" for that.
    errorNotification: false,
  });
  // A project with no chat sessions answers null, hence the optional reads.
  const summary = data?.data;

  return (
    <StatSummary
      heading={t(`${KEY}.heading`, { days: DAYS })}
      subtitle={t(`${KEY}.subtitle`)}
      extra={
        projectId ? <ViewChatHistoryLink projectId={projectId} /> : undefined
      }
      loading={isLoading}
      stats={[
        {
          // Counts drive the label's plural, so "1 Conversation" reads right.
          label: t(`${KEY}.conversations`, {
            count: summary?.total_chat_sessions ?? 0,
          }),
          value: formatCount(summary?.total_chat_sessions),
        },
        {
          label: t(`${KEY}.questionsPerSession`, {
            count: summary?.avg_messages_sent ?? 0,
          }),
          value: formatAverage(summary?.avg_messages_sent),
        },
        {
          label: t(`${KEY}.chatEngagement`),
          value: formatPct(summary?.chat_rate_pct),
        },
      ]}
    />
  );
};
